/**
 * «Найти» (§2.2.4): пищалка, фонарик, сирена — вторичные кнопки, меньше
 * замка и статусов, потому что отвечают на другой вопрос («который из этих
 * байков мой»), а не управляют байком.
 *
 * Все три — мимо офлайн-очереди, как весь `api/device.ts`: живая
 * радиокоманда, а не бизнес-состояние. Сирена (C3) ещё не везде выкачена —
 * 404 читается тем же `commandErrorText`, что и остальные команды.
 */
import { useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { useRingVehicle } from '@/api/vehicles'
import {
  commandErrorText,
  isCommandOk,
  useSetHeadlight,
  useSiren,
  type HeadlightState,
} from '@/api/device'

const SIREN_SECONDS = 60

function SmallButton({
  disabled,
  busy,
  onClick,
  children,
}: {
  disabled?: boolean
  busy?: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      disabled={disabled || busy}
      onClick={onClick}
      className="min-h-11 flex-1 rounded-lg border border-white/20 px-3 text-sm font-medium
        text-white/80 transition active:bg-white/10 disabled:opacity-40"
    >
      {busy ? '…' : children}
    </button>
  )
}

export function BikeFind({
  vehicleId,
  online,
  /** Партнёру фонарик закрыт (staff-ярус) — см. lib/auth.tsx. */
  showHeadlight,
  /** Байк из своего пула: крупная сирена уже в панели пула, вторая не нужна. */
  showSiren = true,
}: {
  vehicleId: string
  online: boolean
  showHeadlight: boolean
  showSiren?: boolean
}) {
  const ring = useRingVehicle(vehicleId)
  const headlight = useSetHeadlight(vehicleId)
  const siren = useSiren(vehicleId)
  const [headlightOn, setHeadlightOn] = useState<boolean | null>(null)

  function toggleHeadlight() {
    const next: HeadlightState = headlightOn ? 'off' : 'on'
    headlight.mutate(next, {
      onSuccess: (out) => {
        if (isCommandOk(out.status)) setHeadlightOn(next === 'on')
        else toast.error(`Фонарик: ${out.status}`)
      },
      onError: (e) => {
        toast.error(commandErrorText(e))
      },
    })
  }

  return (
    <section className="mb-4">
      <h2 className="mb-2 text-sm font-medium uppercase tracking-wide opacity-50">Найти</h2>
      <div className="flex gap-2">
        <SmallButton
          disabled={!online}
          busy={ring.isPending}
          onClick={() => {
            ring.mutate(undefined, {
              onError: (e) => {
                toast.error(commandErrorText(e))
              },
            })
          }}
        >
          Пищалка
        </SmallButton>
        {showHeadlight ? (
          <SmallButton disabled={!online} busy={headlight.isPending} onClick={toggleHeadlight}>
            {headlightOn ? 'Фонарик · выкл' : 'Фонарик'}
          </SmallButton>
        ) : null}
        {showSiren ? (
          <SmallButton
            disabled={!online}
            busy={siren.isPending}
            onClick={() => {
              siren.mutate(SIREN_SECONDS, {
                onSuccess: () => {
                  toast('Сирена включена на 60 с')
                },
                onError: (e) => {
                  toast.error(commandErrorText(e))
                },
              })
            }}
          >
            Сирена 60 с
          </SmallButton>
        ) : null}
      </div>
    </section>
  )
}
